// src/core/mvcs/view/components/PhoenixFlameView.ts
import { Particle, ParticleContainer, Point, Rectangle, Texture } from 'pixi.js';
import { GameConfig } from '../../../config/GameConfig';
import { TaskView } from '../TaskView';
import type { IFlameParticle } from './meta/IFlameParticle';

/**
 * View responsible for the "Phoenix Flame" effect.
 * Uses an object pooling strategy to maintain exactly 10 sprites.
 */
export class PhoenixFlameView extends TaskView {
    private readonly cfg = GameConfig.FLAME;

    private readonly tempPoint = new Point(); // PRE-ALLOCATED: No GC on mouse move
    private readonly PI = Math.PI; // Cached PI

    private particleContainer!: ParticleContainer;

    private readonly particlesWarper: IFlameParticle[] = [];
    private readonly emitterPos = { x: 0, y: 0 };

    /**
     * Initializes the PhoenixFlameView.
     * Sets the event mode to 'static' and adds event listeners for pointer movement and pointer down events.
     */
    public override init(): void {
        super.init();

        this.initEvents();
        this.initParticlesContainer();
    }

    /**
     * Initializes the event listeners for the PhoenixFlameView.
     * Sets the event mode to 'static' and adds event listeners for the 'pointermove' and 'pointerdown' events.
     * The event handler for these events is onPointerHandler.
     */
    private initEvents(): void {
        this.eventMode = 'static';

        this.on('pointermove', this.onPointerHandler, this);
        this.on('pointerdown', this.onPointerHandler, this);
    }

    /**
     * Initializes the ParticleContainer instance used by the PhoenixFlameView.
     * By default, the ParticleContainer has dynamic properties enabled for position, rotation, vertex, UVs, and color.
     * The ParticleContainer is also set as a render group and uses the 'add' blend mode.
     * Finally, the ParticleContainer is added as a child to the PhoenixFlameView.
     */
    private initParticlesContainer(): void {
        console.debug("[PhoenixFlameView] Creating particle container.");

        this.particleContainer = new ParticleContainer({
            dynamicProperties: {
                // (dynamic for maximum speed)
                position: true, // change x and y
                rotation: true, // change rotation
                color: true,    // This handles both TINT and ALPHA in one buffer
                vertex: true,   // Required for scaling (scaleX/scaleY)
                // (static for maximum speed)
                uvs: false,
                texture: false, // All particles use the same flame texture
                blendMode: false,// All particles use 'add'
                anchor: false,  // Anchors are set once at init
            }
        });

        // Set default particle options
        Particle.defaultOptions = {
            anchorX: 0.5,
            anchorY: 0.5,
            alpha: 1,
            scaleX: 1,
            scaleY: 1
        };

        this.particleContainer.isRenderGroup = true;
        this.particleContainer.blendMode = this.cfg.FLAME_BLEND_MODE;

        this.addChild(this.particleContainer);
    }

    /**
     * Removes all event listeners and cleans up the view for garbage collection.
     * Should be called when the view is no longer needed.
     */
    public override dispose(): void {
        super.dispose();

        this.off('pointermove', this.onPointerHandler, this);
        this.off('pointerdown', this.onPointerHandler, this);

        // Clear references for GC
        this.particlesWarper.length = 0;
    }

    /**
     * Initializes the flame effect by creating MAX_PARTICLES sprites from the given texture.
     * Each sprite is given a random life, speed, and x offset to create a staggered start.
     * The sprites are then added to the view and stored in the particles array.
     * 
     * @param texture - Texture to use for the flame effect.
     */
    public setupFlameParticleEmitter(flameTexture: Texture): void {
        console.debug("[PhoenixFlameView] Setting up flame particle emitter.");

        for (let i = 0; i < this.cfg.MAX_PARTICLES; i++) {
            const particle = new Particle(flameTexture);
            const flameParticleWarper: IFlameParticle = {
                item: particle,
                life: Math.random(), // Staggered start so particles don't sync
                speed: 1 + Math.random() * this.cfg.FLAME_DRIFT_SPEED,
                xOffset: 0
            };

            this.resetParticle(flameParticleWarper);
            this.particlesWarper.push(flameParticleWarper);

            this.particleContainer.addParticle(particle);
        }
    }

    /**
     * Updates the flame particles based on the time delta.
     * Adjusts position, scale, tint, alpha, and rotation for each particle.
     * Particles are reset when their life reaches zero.
     * 
     * @param delta - Time difference in milliseconds since the last frame update.
     */
    public update(delta: number): void {
        const driftForce = 0.008 * delta;
        // Classic loop for performance
        const particlesLength = this.particlesWarper.length;
        for (let i = 0; i < particlesLength; i++) {
            const particle = this.particlesWarper[i];
            particle.life -= driftForce * particle.speed;

            particle.item.y -= particle.speed * delta * 2.5;
            particle.item.x = particle.xOffset + Math.sin(particle.item.y * 0.05) * 15;

            const size = Math.sin(particle.life * this.PI) * 2.5;
            particle.item.scaleX = particle.item.scaleY = size;

            particle.item.alpha = particle.life;

            if (particle.life > 0.6) {
                particle.item.tint = this.cfg.FLAME_BASE_COLOR; // White hot base
            } else if (particle.life > 0.3) {
                particle.item.tint = this.cfg.FLAME_CORE_COLOR; // Orange core
            } else {
                particle.item.tint = this.cfg.FLAME_EMBERS_COLOR; // Cooling red embers
            }

            particle.item.rotation = Math.cos(particle.item.y * 0.05) * 0.2;

            if (particle.life <= 0) {
                this.resetParticle(particle);
            }
        }
    }

    /**
     * Resets a flame particle to its default state.
     * Particles are given a new random life, speed, and x offset to create a natural spread.
     * This method is called when a particle's life reaches zero.
     * 
     * @param particle - The particle to reset.
     */
    private resetParticle(particle: IFlameParticle): void {
        particle.life = 1;
        // Particles spawn at the emitter position with a slight random spread
        particle.xOffset = this.emitterPos.x + (Math.random() - 0.5) * 30;
        particle.item.x = particle.xOffset;
        particle.item.y = this.emitterPos.y;
        particle.speed = 1 + Math.random() * 1.5;
    }

    /**
     * Called by the Mediator or Parent during the render phase.
     * Positions the emitter at the center of the screen (50%, 85%).
     * If the emitter position has not been set by interaction, it sets a default value.
     * 
     * @param width Width of the screen
     * @param height Height of the screen
     */
    public override layout(width: number, height: number): void {
        this.position.set(0, 0);

        this.hitArea = new Rectangle(0, 0, width, height);

        // Default position if not yet set by interaction
        if (this.emitterPos.x === 0 && this.emitterPos.y === 0) {
            this.setEmitterPosition(width * 0.5, height * 0.85);
        }

        // Too much logging (enable if needed)
        //console.debug(`[PhoenixFlameView] Using responsive layout. View positioned at (${this.x}, ${this.y})`);
    }

    /**
     * Updates the emitter position based on the mouse pointer.
     */
    private onPointerHandler(event: any): void {
        event.getLocalPosition(this, this.tempPoint);
        this.setEmitterPosition(this.tempPoint.x, this.tempPoint.y);
    }

    /**
     * Updates the emitter position.
     */
    public setEmitterPosition(x: number, y: number): void {
        this.emitterPos.x = x;
        this.emitterPos.y = y;
    }
}